/**
 * 📱 Viewport Height Utility для мобильных браузеров
 * Решает проблему с изменяющейся высотой в Safari и других мобильных браузерах
 */

import { useState, useEffect } from "react"
import { debugLog, debugWarn } from "@/lib/debug"

let viewportHeightCallback: ((height: number) => void) | null = null
let isInitialized = false
let originalViewportHeight = 0
let currentScrollCompensation = 0

/**
 * 🔧 Устанавливает фиксированную высоту viewport для мобильных браузеров
 */
function setViewportHeight() {
  if (typeof window === 'undefined') return
  
  // Получаем актуальную высоту окна
  let vh = window.innerHeight
  
  // Для мобильных устройств: если есть visual viewport API, используем его
  if ('visualViewport' in window && window.visualViewport) {
    vh = window.visualViewport.height
  }
  
  // Устанавливаем CSS custom property
  document.documentElement.style.setProperty('--vh', `${vh}px`)
  document.documentElement.style.setProperty('--real-100vh', `${vh}px`)
  
  // Вызываем callback если есть
  if (viewportHeightCallback) {
    viewportHeightCallback(vh)
  }
  
  debugLog('performance', '📱 [Viewport] Высота обновлена:', vh + 'px')
}

/**
 * 🔄 Компенсация изменения высоты через скролл ScrollArea
 */
function compensateScrollArea(heightDifference: number) {
  // Ищем ScrollArea на странице дашборда
  const scrollArea = document.querySelector('[data-slot="scroll-area-viewport"]') as HTMLElement
  if (!scrollArea) {
    debugLog('performance', '📱 [Viewport] ScrollArea не найден')
    return
  }
  
  // Выполняем компенсацию (проверка позиции уже выполнена в handleInputFocus)
  const currentScrollTop = scrollArea.scrollTop
  const newScrollTop = Math.max(0, currentScrollTop + heightDifference)
  
  debugLog('performance', '📱 [Viewport] Выполняем компенсацию скролла:', {
    heightDifference,
    currentScrollTop,
    newScrollTop
  })
  
  scrollArea.scrollTo({
    top: newScrollTop,
    behavior: 'smooth'
  })
  
  currentScrollCompensation = heightDifference
}

/**
 * 🚀 Инициализация viewport utility
 */
export function initViewportHeight(callback?: (height: number) => void): () => void {
  if (typeof window === 'undefined') return () => {}
  
  if (isInitialized) {
    debugWarn('performance', '⚠️ [Viewport] Уже инициализировано')
    return () => {}
  }
  
  isInitialized = true
  viewportHeightCallback = callback || null
  
  debugLog('performance', '🚀 [Viewport] Инициализация...')
  
  // Устанавливаем начальную высоту и сохраняем как исходную
  setViewportHeight()
  
  // Сохраняем исходную высоту для расчета компенсации
  originalViewportHeight = window.innerHeight
  if ('visualViewport' in window && window.visualViewport) {
    originalViewportHeight = window.visualViewport.height
  }
  
  debugLog('performance', '📱 [Viewport] Исходная высота сохранена:', originalViewportHeight + 'px')
  
  // Слушаем изменения размера окна
  let resizeTimeout: NodeJS.Timeout
  const handleResize = () => {
    // Дебаунсим для производительности
    clearTimeout(resizeTimeout)
    resizeTimeout = setTimeout(setViewportHeight, 50)
  }
  
  const handleVisualViewportChange = () => {
    debugLog('performance', '[Viewport] handleVisualViewportChange')
    // Для visual viewport изменений (клавиатура) - без дебаунса для быстроты
    setViewportHeight()
  }
  
  window.addEventListener('resize', handleResize)
  window.addEventListener('orientationchange', setViewportHeight)
  
  // БЛОКИРУЕМ VISUAL VIEWPORT СКРОЛЛ ПОЛНОСТЬЮ
  const blockVisualViewportScroll = () => {
    if (window.visualViewport && window.visualViewport.offsetTop !== 0) {
      debugLog('performance', "[Viewport] blockVisualViewportScroll", window.visualViewport.offsetTop, document.body.scrollTop, document.documentElement.scrollTop)
      // Пытаемся сбросить offsetTop (не всегда работает, зависит от браузера)
      window.scrollTo(0, 0)
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }
  }

  // Дополнительно для iOS Safari и виртуальной клавиатуры
  if ('visualViewport' in window && window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleVisualViewportChange)
    window.visualViewport.addEventListener('scroll', blockVisualViewportScroll)
  }
  
  // Типы нативных пикеров — date/time/month и пр. управляют своей высотой сами,
  // компенсация скролла для них не нужна и только вносит артефакты.
  const NATIVE_PICKER_INPUT_TYPES = new Set(['date', 'time', 'datetime-local', 'month', 'week'])

  // Обработчики для input элементов (фокус/потеря фокуса)
  const handleInputFocus = (e: Event) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      // Нативные date/time пикеры — пропускаем, они не используют экранную клавиатуру.
      if (e.target instanceof HTMLInputElement && NATIVE_PICKER_INPUT_TYPES.has(e.target.type)) return

      debugLog('performance', '📱 [Viewport] Input получил фокус')
      
      const inputElement = e.target as HTMLElement
      
      // Сохраняем позицию input'а ДО появления клавиатуры
      const inputRectBefore = inputElement.getBoundingClientRect()
      
      // Сохраняем исходную высоту до появления клавиатуры
      let beforeKeyboardHeight = window.innerHeight
      if ('visualViewport' in window && window.visualViewport) {
        beforeKeyboardHeight = window.visualViewport.height
      }
      
      debugLog('performance', '📱 [Viewport] До клавиатуры:', {
        inputBottom: Math.round(inputRectBefore.bottom),
        viewportHeight: beforeKeyboardHeight
      })
      
      // Небольшая задержка для появления клавиатуры
      setTimeout(() => {
        // Измеряем новую высоту после появления клавиатуры
        let afterKeyboardHeight = window.innerHeight
        if ('visualViewport' in window && window.visualViewport) {
          afterKeyboardHeight = window.visualViewport.height
        }
        
        // Вычисляем разницу в пикселях
        const heightDifference = beforeKeyboardHeight - afterKeyboardHeight
        
        debugLog('performance', '📱 [Viewport] Разница высоты от клавиатуры:', {
          before: beforeKeyboardHeight,
          after: afterKeyboardHeight,
          difference: heightDifference
        })
        
        // Если есть разница - проверяем нужна ли компенсация по позиции
        if (heightDifference > 0) {
          // Используем позицию input'а до появления клавиатуры для корректной проверки
          const keyboardTop = beforeKeyboardHeight - heightDifference
          const needsCompensation = inputRectBefore.bottom > keyboardTop
          
          debugLog('performance', '📱 [Viewport] Проверка необходимости компенсации:', {
            inputBottom: Math.round(inputRectBefore.bottom),
            keyboardTop: Math.round(keyboardTop),
            needsCompensation
          })
          
          if (needsCompensation) {
            compensateScrollArea(heightDifference)
          } else {
            debugLog('performance', '📱 [Viewport] Input выше клавиатуры - компенсация не нужна')
          }
        }
        
        setViewportHeight()
      }, 100)
    }
  }
  
  const handleInputBlur = (e: Event) => {
    // if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
    //   console.log('📱 [Viewport] Input потерял фокус')
      
    //   // Убираем компенсацию скролла только если она была применена
    //   if (currentScrollCompensation > 0) {
    //     setTimeout(() => {
    //       const scrollArea = document.querySelector('[data-slot="scroll-area-viewport"]') as HTMLElement
    //       if (scrollArea) {
    //         const currentScrollTop = scrollArea.scrollTop
    //         const newScrollTop = Math.max(0, currentScrollTop - currentScrollCompensation)
            
    //         console.log('📱 [Viewport] Возврат скролла:', {
    //           compensation: currentScrollCompensation,
    //           currentScrollTop,
    //           newScrollTop
    //         })
            
    //         scrollArea.scrollTo({
    //           top: newScrollTop,
    //           behavior: 'smooth'
    //         })
    //       }
          
    //       currentScrollCompensation = 0
    //       setViewportHeight()
    //     }, 100)
    //   } else {
    //     setViewportHeight()
    //   }
    // }
  }
  
  // Обработчик для предотвращения автоскролла document'а (только на мобильных)
  const preventDocumentScroll = (e: Event) => {
    // Предотвращаем скролл на document уровне только если это мобильное устройство
    if (window.innerWidth <= 767 && (e.target === document || e.target === document.documentElement || e.target === document.body)) {
      e.preventDefault()
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }
  }
  
  // Добавляем обработчики для всех input/textarea элементов
  document.addEventListener('focusin', handleInputFocus)
  document.addEventListener('focusout', handleInputBlur)
  document.addEventListener('scroll', preventDocumentScroll, { passive: false })
  
  // Cleanup функция
  return () => {
    debugLog('performance', '🧹 [Viewport] Cleanup...')
    window.removeEventListener('resize', handleResize)
    window.removeEventListener('orientationchange', setViewportHeight)
    if ('visualViewport' in window && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handleVisualViewportChange)
      window.visualViewport.removeEventListener('scroll', blockVisualViewportScroll)
    }
    document.removeEventListener('focusin', handleInputFocus)
    document.removeEventListener('focusout', handleInputBlur)
    document.removeEventListener('scroll', preventDocumentScroll)
    clearTimeout(resizeTimeout)
    isInitialized = false
    viewportHeightCallback = null
    originalViewportHeight = 0
    currentScrollCompensation = 0
  }
}

/**
 * 🔄 Принудительное обновление высоты
 */
export function updateViewportHeight() {
  if (!isInitialized) {
    debugWarn('performance', '⚠️ [Viewport] Не инициализировано, вызовите initViewportHeight() сначала')
    return
  }
  setViewportHeight()
}

/**
 * 📊 Получить текущую высоту viewport
 */
export function getCurrentViewportHeight(): number {
  if (typeof window === 'undefined') return 0
  return window.innerHeight
}

/**
 * 🔧 Хук для React компонентов
 */
export function useViewportHeight() {
  const [height, setHeight] = useState(() => {
    if (typeof window === 'undefined') return 0
    return getCurrentViewportHeight()
  })
  
  useEffect(() => {
    if (typeof window === 'undefined') return
    const cleanup = initViewportHeight(setHeight)
    return cleanup
  }, [])
  
  return height
}
